import { Button, Loader, Stack, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { useGetDeploymentOverview } from '@/queries/deploymentQueries'
import {
  GPU_MEMORY_REFETCH_INTERVAL,
  useGetDeviceGpuMemory
} from '@/queries/deviceQueries'
import { Device } from '@/types/device'
import {
  SavedVlmGpus,
  getGpuSelectionError,
  getHeldGpuMemory,
  getOfferedGpus
} from '../../helpers/gpuSelection'
import { FormValues } from '../DeploymentSettings'
import { SettingsCard } from '../SettingsCard'
import { DeviceGpuPicker } from './DeviceGpuPicker'

type DeviceGpuSectionProps = {
  device: Device
  value: number[]
  held?: Record<number, number>
  savedVlmGpus?: number[]
  error?: string
  onChange: (value: number[]) => void
}

const DeviceGpuSection = ({
  device,
  value,
  held,
  savedVlmGpus,
  error,
  onChange
}: DeviceGpuSectionProps) => {
  const offeredIndexes = getOfferedGpus(device, savedVlmGpus).map(
    (gpu) => gpu.index
  )

  const { data: memory } = useGetDeviceGpuMemory(
    device.id,
    offeredIndexes,
    offeredIndexes.length > 0
  )

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        {device.label || device.name}
      </Text>

      <DeviceGpuPicker
        device={device}
        value={value}
        savedVlmGpus={savedVlmGpus}
        memory={memory}
        held={held}
        error={error}
        onChange={onChange}
      />
    </Stack>
  )
}

type GroupGpuSettingsProps = {
  devices?: Device[]
  savedVlmGpus?: SavedVlmGpus
  isLoading: boolean
  loadFailed?: boolean
  onRetry?: () => void
  form: UseFormReturnType<FormValues>
}

export const GroupGpuSettings = ({
  devices,
  savedVlmGpus = {},
  isLoading,
  loadFailed = false,
  onRetry,
  form
}: GroupGpuSettingsProps) => {
  const intl = useIntl()
  const { application } = useApplicationContext()
  const selection = form.getValues().gpuSelection
  const { data: overview } = useGetDeploymentOverview(
    { application_id: application?.id, limit: 1 },
    {
      enabled: Boolean(application?.id),
      refetchInterval: GPU_MEMORY_REFETCH_INTERVAL
    }
  )
  const held = getHeldGpuMemory(overview, application?.id)

  return (
    <SettingsCard
      title={<FormattedMessage id="deployments.gpuSettings.title" />}
      description={
        <FormattedMessage id="deployments.gpuSettings.groups.description" />
      }
    >
      {isLoading && <Loader size="sm" />}

      {loadFailed && (
        <PlatformAlert variant="error" compact>
          <Stack gap="xs" align="flex-start">
            <FormattedMessage id="deployments.gpuSettings.loadFailed" />
            <Button size="xs" variant="outline" onClick={onRetry}>
              <FormattedMessage id="tryAgain" />
            </Button>
          </Stack>
        </PlatformAlert>
      )}

      {devices && (
        <Stack gap="lg">
          {devices.map((device) => (
            <DeviceGpuSection
              key={device.id}
              device={device}
              value={selection[device.id] ?? []}
              held={held[device.id]}
              savedVlmGpus={savedVlmGpus[device.id]}
              error={
                getGpuSelectionError(
                  selection,
                  device,
                  savedVlmGpus[device.id]
                ) === 'nothingSelected'
                  ? intl.formatMessage({
                      id: 'deployments.gpuSettings.selectForDevice'
                    })
                  : undefined
              }
              onChange={(value) =>
                form.setFieldValue('gpuSelection', {
                  ...selection,
                  [device.id]: value
                })
              }
            />
          ))}
        </Stack>
      )}
    </SettingsCard>
  )
}
